import { Button } from '@/components/common/Buttons';
import { CtaCard } from '@/components/common/CtaCard';
import { PortableText } from '@/components/common/utils';
import { Section } from '@/components/layout/Section';
import { inferButtonComponentTheme } from '@/lib/colourUtils';
import { blockUrlWithAudience } from '@/lib/utils';

import type {
  SanityAudienceType,
  SanityCtaCardType,
  SanityKeyed,
} from '@/types/sanity';
import type { BlockWithAudience, TrackingTagType } from '@/types/shared';

export interface CtaCardBlockProps
  extends Omit<SanityCtaCardType, 'actions'>,
    BlockWithAudience {
  actions?: Array<
    SanityKeyed<{
      _type: 'actionItem';
      buttonLabel: string;
      buttonAccessibleLabel?: string;
      audienceRef?: SanityAudienceType;
      noAudienceToggle?: boolean;
      buttonLink: string;
      buttonTheme: 'dark' | 'light';
      buttonVariant?: 'primary' | 'secondary';
      trackingTag?: TrackingTagType;
    }>
  >;
}

export const CtaCardBlock = ({
  heading,
  body,
  actions,
  blockBackgroundColour,
  blockSpacing,
  paddingAdjustment,
  backgroundColour,
  pageAudience,
  isAudienceSwitcherEnabled,
}: CtaCardBlockProps) => {
  const blockBackgroundColourToken = blockBackgroundColour?.token;
  const backgroundColourToken = backgroundColour?.token;

  return (
    <Section
      verticalPadding={paddingAdjustment ?? true}
      spacing={blockSpacing}
      backgroundColour={blockBackgroundColourToken}
      className="cta-card"
    >
      <CtaCard
        backgroundColour={backgroundColourToken}
        heading={heading}
        body={<PortableText value={body} />}
        actions={
          !!actions && (
            <>
              {actions.map(
                ({
                  buttonLabel,
                  buttonVariant,
                  buttonTheme,
                  buttonLink,
                  buttonAccessibleLabel,
                  trackingTag,
                  audienceRef,
                  noAudienceToggle,
                }) => {
                  return (
                    <Button
                      key={buttonLabel}
                      iconPosition="right"
                      href={blockUrlWithAudience(
                        buttonLink,
                        audienceRef,
                        pageAudience,
                        noAudienceToggle,
                        isAudienceSwitcherEnabled,
                      )}
                      aria-label={buttonAccessibleLabel}
                      className={
                        trackingTag?.blockName &&
                        `tracking-${trackingTag.blockName}__${trackingTag.blockAction}`
                      }
                      variant={inferButtonComponentTheme(
                        buttonTheme,
                        buttonVariant,
                      )}
                    >
                      {buttonLabel}
                    </Button>
                  );
                },
              )}
            </>
          )
        }
      />
    </Section>
  );
};
